import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/auth';
import { updateUserDetailsService } from '../services/auth.service';

/**
 * The "?" guide dots (My Schools, My Classes) — remembered PER USER in the database (users.seen_hints,
 * beside show_insider_tutorial), so a hint seen once stays seen on every
 * device and after any logout. They used to live in localStorage: per browser,
 * shared by everyone on it, and wiped by a forced logout.
 *
 * @param {string} key        the hint's key in users.seen_hints, e.g. 'pw_my_schools_guide'
 * @param {string} [legacyKey] the localStorage key it used before. A hint already
 *   seen on this browser counts as seen, and is copied to the database once.
 * @returns {[boolean, Function]} [seen, markSeen]
 */
export const useSeenHint = (key, legacyKey = null) => {
    const { authUser, setAuthUser } = useAuth();
    const inDb = Array.isArray(authUser?.seen_hints) && authUser.seen_hints.includes(key);
    let inLegacy = false;
    if (legacyKey) {
        try { inLegacy = localStorage.getItem(legacyKey) === '1'; } catch (e) { /* storage unavailable */ }
    }
    const seen = inDb || inLegacy;

    const saving = useRef(false);
    const markSeen = useCallback(() => {
        if (!authUser || saving.current) return;
        if (Array.isArray(authUser.seen_hints) && authUser.seen_hints.includes(key)) return;
        saving.current = true;
        // At once on screen; the database follows.
        setAuthUser({ ...authUser, seen_hints: [...(authUser.seen_hints || []), key] });
        updateUserDetailsService({ mark_seen: key })
            .catch(() => {
                // Not saved: at least this browser remembers it, as before.
                if (legacyKey) { try { localStorage.setItem(legacyKey, '1'); } catch (e) { /* ignore */ } }
            })
            .finally(() => { saving.current = false; });
    }, [authUser, setAuthUser, key, legacyKey]);

    // Seen on this browser before the move to the database — record it once.
    useEffect(() => {
        if (inLegacy && !inDb && authUser) markSeen();
    }, [inLegacy, inDb]); // eslint-disable-line react-hooks/exhaustive-deps

    return [seen, markSeen];
};

/** Keys in users.seen_hints. One place, so a key is never spelled two ways. */
export const SEEN_HINT = {
    PW_MY_SCHOOLS_GUIDE:  'pw_my_schools_guide',
    SNW_MY_SCHOOLS_GUIDE: 'snw_my_schools_guide',
    MY_CLASSES_GUIDE:     'sw_my_classes_guide',
};
